import { Bot } from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import type { CadenceDelegatedAgent } from "./types";

type TrackBucket = "running" | "failed";
type TrackPanelPlacement = "top" | "bottom";

const PANEL_OFFSET = 12;
const PANEL_MAX_HEIGHT = 440;
const VIEWPORT_EDGE_PADDING = 8;

// PASEO's provider-child presentation marks only active work and failures.
// VCode still retains pending/interrupted lifecycle facts in the timeline, but
// projecting them as a third ambient pill state would invent an attention
// contract Cadence has not received from the Runtime.
const bucketOrder: TrackBucket[] = ["failed", "running"];
const compactFormFactorQuery = "(max-width: 719px)";

function usePaseoCompactFormFactor(): boolean {
  const [compact, setCompact] = useState(() =>
    typeof window !== "undefined" && window.matchMedia(compactFormFactorQuery).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(compactFormFactorQuery);
    const update = () => setCompact(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return compact;
}

function bucketForStatus(status: CadenceDelegatedAgent["status"]): TrackBucket | undefined {
  if (status === "working") return "running";
  if (status === "error") return "failed";
  return undefined;
}

function bucketLabel(bucket: TrackBucket, count: number): string {
  switch (bucket) {
    case "running":
      return `${count} 个正在工作`;
    case "failed":
      return `${count} 个失败`;
  }
}

function TrackMark({ bucket }: { bucket: TrackBucket | undefined }) {
  return bucket ? (
    <span className="delegated-agent-track-mark" data-status={bucket} aria-hidden="true" />
  ) : null;
}

/**
 * PASEO-compatible delegated-agent ambient track.
 *
 * It only opens a read-only child timeline. The parent record and a child
 * thread are intentionally separate: one child row is keyed by the stable
 * VCode thread route even when the Project event stream contains many records.
 */
export function DelegatedAgentsTrack({
  agents,
  onOpenAgent,
}: {
  agents: CadenceDelegatedAgent[];
  onOpenAgent(agent: CadenceDelegatedAgent): void;
}) {
  const [open, setOpen] = useState(false);
  const [panelPlacement, setPanelPlacement] = useState<TrackPanelPlacement>("top");
  const [panelMaxHeight, setPanelMaxHeight] = useState<number | null>(null);
  const [panelPosition, setPanelPosition] = useState<{ left: number; top: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const sheetTitleId = useId();
  const compact = usePaseoCompactFormFactor();
  const statusCounts = useMemo(
    () =>
      bucketOrder.flatMap((bucket) => {
        const count = agents.filter((agent) => bucketForStatus(agent.status) === bucket).length;
        return count ? [{ bucket, count }] : [];
      }),
    [agents],
  );

  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) {
      window.setTimeout(() => triggerRef.current?.focus(), 0);
    }
  }, []);

  const positionPanel = useCallback(() => {
    if (compact) return;
    const trigger = triggerRef.current?.getBoundingClientRect();
    const panel = panelRef.current?.getBoundingClientRect();
    if (!trigger || !panel) return;

    const maxHeight = Math.max(
      0,
      Math.min(PANEL_MAX_HEIGHT, window.innerHeight - VIEWPORT_EDGE_PADDING * 2),
    );
    const panelHeight = Math.min(panel.height, maxHeight);
    const spaceTop = trigger.top;
    const spaceBottom = window.innerHeight - trigger.bottom;
    // Keep PASEO's top-first rule: a child-thread list only moves below its
    // pill when that side has more room. Otherwise clamp the requested top
    // position instead of making the list jump between two cramped sides.
    const placement: TrackPanelPlacement =
      spaceTop < panelHeight && spaceBottom > spaceTop ? "bottom" : "top";
    const requestedTop =
      placement === "bottom"
        ? trigger.bottom + PANEL_OFFSET
        : trigger.top - panelHeight - PANEL_OFFSET;
    const left = Math.max(
      VIEWPORT_EDGE_PADDING,
      Math.min(window.innerWidth - panel.width - VIEWPORT_EDGE_PADDING, trigger.left),
    );
    const top = Math.max(
      VIEWPORT_EDGE_PADDING,
      Math.min(window.innerHeight - panelHeight - VIEWPORT_EDGE_PADDING, requestedTop),
    );

    setPanelPlacement(placement);
    setPanelMaxHeight((current) => (current === maxHeight ? current : maxHeight));
    setPanelPosition((current) =>
      current?.left === left && current.top === top ? current : { left, top },
    );
  }, [compact]);

  useLayoutEffect(() => {
    if (!open || compact) {
      setPanelPlacement("top");
      setPanelMaxHeight(null);
      setPanelPosition(null);
      return undefined;
    }

    positionPanel();
    window.addEventListener("resize", positionPanel);
    const resizeObserver =
      typeof ResizeObserver === "undefined"
        ? undefined
        : new ResizeObserver(() => positionPanel());
    if (panelRef.current) resizeObserver?.observe(panelRef.current);
    return () => {
      window.removeEventListener("resize", positionPanel);
      resizeObserver?.disconnect();
    };
  }, [agents, compact, open, positionPanel]);

  useEffect(() => {
    if (!open) return;
    const previousBodyOverflow = document.body.style.overflow;
    if (compact) document.body.style.overflow = "hidden";
    const focusFrame = window.requestAnimationFrame(() => {
      panelRef.current
        ?.querySelector<HTMLButtonElement>("[data-delegated-agent-track-row]")
        ?.focus();
    });
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        close(true);
        return;
      }
      const panel = panelRef.current;
      if (!panel?.contains(document.activeElement)) return;
      const rows = Array.from(
        panel.querySelectorAll<HTMLButtonElement>("[data-delegated-agent-track-row]"),
      );
      if (!rows.length) return;
      const currentIndex = rows.findIndex((row) => row === document.activeElement);
      if (compact && event.key === "Tab") {
        event.preventDefault();
        const nextIndex = event.shiftKey
          ? currentIndex <= 0 ? rows.length - 1 : currentIndex - 1
          : currentIndex >= rows.length - 1 ? 0 : currentIndex + 1;
        rows[nextIndex]?.focus();
        return;
      }
      const nextIndex =
        event.key === "ArrowDown"
          ? (currentIndex + 1 + rows.length) % rows.length
          : event.key === "ArrowUp"
            ? (currentIndex - 1 + rows.length) % rows.length
            : event.key === "Home"
              ? 0
              : event.key === "End"
                ? rows.length - 1
                : undefined;
      if (nextIndex === undefined) return;
      event.preventDefault();
      rows[nextIndex]?.focus();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      if (focusFrame !== undefined) window.cancelAnimationFrame(focusFrame);
      if (compact) document.body.style.overflow = previousBodyOverflow;
    };
  }, [compact, open, close]);

  if (!agents.length) return null;

  const label = statusCounts.length
    ? statusCounts.map(({ bucket, count }) => bucketLabel(bucket, count)).join("，")
    : agents.length === 1
      ? "1 个子智能体"
      : `${agents.length} 个子智能体`;

  return (
    <div className="delegated-agent-track" data-testid="delegated-agent-track">
      <div className="delegated-agent-track-rail">
        <button
          ref={triggerRef}
          type="button"
          className="delegated-agent-track-pill"
          data-testid="delegated-agent-track-header"
          aria-expanded={open}
          aria-controls={panelId}
          aria-label={label}
          onClick={() => {
            if (open) {
              close(true);
            } else {
              setOpen(true);
            }
          }}
        >
          {statusCounts.length ? (
            statusCounts.map(({ bucket, count }) => (
              <span className="delegated-agent-track-segment" key={bucket}>
                <TrackMark bucket={bucket} />
                <span>{bucketLabel(bucket, count)}</span>
              </span>
            ))
          ) : (
            <span className="delegated-agent-track-segment">{label}</span>
          )}
        </button>
        {open && (
          <>
            <button
              className="delegated-agent-track-backdrop"
              type="button"
              aria-label="关闭协作者列表"
              onClick={() => close(true)}
            />
            <div
              ref={panelRef}
              className="delegated-agent-track-panel"
              data-testid="delegated-agent-track-panel"
              data-side={compact ? undefined : panelPlacement}
              id={panelId}
              role={compact ? "dialog" : "menu"}
              aria-label={compact ? undefined : "协作者"}
              aria-labelledby={compact ? sheetTitleId : undefined}
              aria-modal={compact || undefined}
              style={
                compact || !panelPosition
                  ? undefined
                  : {
                      left: panelPosition.left,
                      top: panelPosition.top,
                      maxHeight: panelMaxHeight ?? PANEL_MAX_HEIGHT,
                    }
              }
            >
              {compact && (
                <>
                  <span className="delegated-agent-track-sheet-handle" aria-hidden="true" />
                  <div className="delegated-agent-track-sheet-header">
                    <h2 id={sheetTitleId}>协作者</h2>
                  </div>
                </>
              )}
              {agents.map((agent) => {
                const bucket = bucketForStatus(agent.status);
                return (
                  <button
                    className="delegated-agent-track-row"
                    data-testid={`delegated-agent-track-row-${agent.childThreadId}`}
                    data-delegated-agent-track-row
                    data-status={agent.status}
                    key={agent.id}
                    role={compact ? undefined : "menuitem"}
                    type="button"
                    onClick={() => {
                      setOpen(false);
                      onOpenAgent(agent);
                    }}
                  >
                    <span className="delegated-agent-track-row-icon" aria-hidden="true">
                      <Bot size={14} />
                      <TrackMark bucket={bucket} />
                    </span>
                    <span className="delegated-agent-track-row-label">{agent.title}</span>
                    <span className="delegated-agent-track-row-subtitle">{agent.subtitle}</span>
                  </button>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
